import React from 'react';
import { Link } from 'react-router-dom';
import { Leaf, Mail, Phone, MapPin } from 'lucide-react';

export const Footer = () => {
    return (
        <footer className="bg-jungle text-sand relative overflow-hidden" data-testid="footer">
            {/* Botanical decoration */}
            <div className="absolute inset-0 opacity-10">
                <svg className="absolute right-0 top-0 w-96 h-96 text-leaf" viewBox="0 0 200 200" fill="currentColor">
                    <path d="M100 20c-10 40-40 70-80 80 40 10 70 40 80 80 10-40 40-70 80-80-40-10-70-40-80-80z" />
                </svg>
                <svg className="absolute left-10 bottom-10 w-64 h-64 text-sage" viewBox="0 0 200 200" fill="currentColor">
                    <path d="M100 30c-8 32-32 56-64 64 32 8 56 32 64 64 8-32 32-56 64-64-32-8-56-32-64-64z" />
                </svg>
            </div>
            
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
                    {/* Brand */}
                    <div className="col-span-1 md:col-span-2">
                        <div className="flex items-center gap-2 mb-6">
                            <div className="w-12 h-12 rounded-xl bg-leaf flex items-center justify-center">
                                <Leaf className="w-7 h-7 text-jungle" />
                            </div>
                            <span className="font-heading font-bold text-2xl">PRIXDEAL</span>
                        </div>
                        <p className="text-sand/70 text-base max-w-md leading-relaxed">
                            Le grossiste de confiance pour les professionnels. Produits frais et de qualité 
                            pour votre commerce.
                        </p>
                        <div className="mt-6 flex gap-3">
                            <span className="inline-flex items-center gap-1 text-xs bg-leaf/20 text-leaf px-3 py-1 rounded-full">
                                <Leaf className="w-3 h-3" /> Frais
                            </span>
                        </div>
                    </div>

                    {/* Links */}
                    <div>
                        <h3 className="font-heading font-semibold text-lg mb-6">Catalogue</h3>
                        <ul className="space-y-3 text-sm text-sand/70">
                            <li><Link to="/catalogue?category=cat-fruits" className="hover:text-leaf transition-colors flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>Fruits</Link></li>
                            <li><Link to="/catalogue?category=cat-legumes" className="hover:text-leaf transition-colors flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>Légumes</Link></li>
                            <li><Link to="/catalogue?category=cat-bio" className="hover:text-leaf transition-colors flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-lime-500"></span>Bio</Link></li>
                            <li><Link to="/catalogue?category=cat-plantes" className="hover:text-leaf transition-colors flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-leaf"></span>Plantes & Fleurs</Link></li>
                        </ul>
                    </div>

                    {/* Contact */}
                    <div>
                        <h3 className="font-heading font-semibold text-lg mb-6">Contact</h3>
                        <ul className="space-y-4 text-sm text-sand/70">
                            <li className="flex items-start gap-3">
                                <div className="w-8 h-8 rounded-lg bg-leaf/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <MapPin className="w-4 h-4 text-leaf" />
                                </div>
                                <div>
                                    <p>CENTRA 307</p>
                                    <p>94586 RUNGIS CEDEX</p>
                                    <p className="mt-1">1 RUE DE LA CORDERIE</p>
                                    <p>94550 CHEVILLY-LARUE</p>
                                </div>
                            </li>
                            <li>
                                <p className="text-sand font-medium mb-3">Administration</p>
                                <div className="space-y-1 ml-2">
                                    <p className="font-medium text-sand">Aisaoui Saber</p>
                                    <p className="flex items-center gap-2">
                                        <Phone className="w-3 h-3 text-leaf" />
                                        06 62 66 76 39
                                    </p>
                                    <p className="flex items-center gap-2">
                                        <Mail className="w-3 h-3 text-leaf" />
                                        asaber@prixdeal.fr
                                    </p>
                                </div>
                            </li>
                            <li>
                                <p className="text-sand font-medium mb-3">Commercial</p>
                                <div className="space-y-3 ml-2">
                                    <div className="space-y-1">
                                        <p className="font-medium text-sand">Khaled Hadjem</p>
                                        <p className="flex items-center gap-2">
                                            <Phone className="w-3 h-3 text-leaf" />
                                            06 19 75 70 55
                                        </p>
                                        <p className="flex items-center gap-2">
                                            <Mail className="w-3 h-3 text-leaf" />
                                            khajjem@prixdeal.fr
                                        </p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="font-medium text-sand">Atef Zarg</p>
                                        <p className="flex items-center gap-2">
                                            <Phone className="w-3 h-3 text-leaf" />
                                            07 64 16 34 96
                                        </p>
                                        <p className="flex items-center gap-2">
                                            <Mail className="w-3 h-3 text-leaf" />
                                            atefprixdeal@gmail.com
                                        </p>
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="border-t border-sand/10 mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-sand/50">
                    <p>&copy; {new Date().getFullYear()} PRIXDEAL. Cultivons ensemble votre succès.</p>
                    <div className="flex flex-wrap justify-center gap-4 text-xs">
                        <Link to="/contact" className="hover:text-leaf transition-colors" data-testid="footer-contact">Nous Contacter</Link>
                        <Link to="/mentions-legales" className="hover:text-leaf transition-colors" data-testid="footer-mentions-legales">Mentions legales</Link>
                        <Link to="/cgv" className="hover:text-leaf transition-colors" data-testid="footer-cgv">CGV</Link>
                        <Link to="/politique-confidentialite" className="hover:text-leaf transition-colors" data-testid="footer-politique-confidentialite">Politique de confidentialite</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
};
